// src/pages/touchPoints/timeline/layout.js
// Grid/stack layout: rows = days, columns = journeys. Each day is one horizontal
// band that hugs its content: every touchpoint on that day sits at the SAME y
// (parallel) in its own column; a sub-journey triggered the same day is pushed one
// slot below its trigger so its connector flows down. Every buffer is a single
// `pad`: pad above a header, pad between header and its card, pad below the last
// card. Header height is per-journey (varies with segment chips) so 2-row headers
// never overlap their card.
import { isoToDays } from './isoDuration';

const DEFAULTS = {
  cardH: 170,    // rendered touchpoint card height (canvas passes the real value)
  headerH: 80,   // fallback header height when no per-journey headerHeightOf is given
  colW: 240, railLeft: 110, topPad: 24,
  pad: 16,       // universal buffer above/below cards and headers
  gap: 24,       // gap between two messages stacked in the same column on the same day
  headerHeightOf: null, // optional (journeyId) => px; falls back to headerH
};

export function layout(model, options = {}) {
  const o = { ...DEFAULTS, ...options };
  const headerH = (jid) => (o.headerHeightOf ? o.headerHeightOf(jid) : o.headerH);
  const cardHeightOf = (tp) => (o.cardHeightOf ? o.cardHeightOf(tp) : o.cardH);

  // Pack journeys into shared columns ("lanes"): two journeys can share a column
  // when their day-ranges don't overlap, so a deep tree of sub-journeys doesn't
  // sprawl sideways. Greedy first-fit by start day keeps the root (earliest) at lane 0.
  const range = {};
  model.journeys.forEach((j) => {
    const ds = j.touchpoints.map((t) => t.day);
    range[j.journeyId] = [Math.min(...ds), Math.max(...ds)];
  });
  const preIndex = {}; model.journeys.forEach((j, i) => { preIndex[j.journeyId] = i; });
  const ordered = [...model.journeys].sort((a, b) => (
    range[a.journeyId][0] - range[b.journeyId][0] || preIndex[a.journeyId] - preIndex[b.journeyId]
  ));
  const laneEnd = []; // last day occupied in each lane
  const colIndex = {};
  ordered.forEach((j) => {
    const [s, e] = range[j.journeyId];
    let lane = laneEnd.findIndex((end) => end < s);
    if (lane === -1) { lane = laneEnd.length; laneEnd.push(e); } else { laneEnd[lane] = e; }
    colIndex[j.journeyId] = lane;
  });
  const laneCount = laneEnd.length;
  const colX = (jid) => o.railLeft + 90 + colIndex[jid] * o.colW;
  const columns = model.journeys.map((j) => ({
    journeyId: j.journeyId, name: j.name, x: colX(j.journeyId),
    primaryTriggerTpId: j.primaryTriggerTpId, isRoot: j.isRoot,
  }));

  const raw = [];
  model.journeys.forEach((j) => j.touchpoints.forEach((tp) => raw.push({
    tp, journeyId: j.journeyId, day: tp.day, isEntry: tp.isEntry, col: colIndex[j.journeyId],
  })));

  // which touchpoint triggers each entry, and what day every touchpoint lands on —
  // used to push a same-day-triggered sub-journey BELOW its trigger so the connector flows down
  const dayOfTp = {}; raw.forEach((r) => { dayOfTp[r.tp.touchpoint_id] = r.day; });
  const entryTrigger = {};
  model.journeys.forEach((j) => {
    const entry = j.touchpoints.find((t) => t.isEntry);
    if (entry && j.primaryTriggerTpId != null) entryTrigger[entry.touchpoint_id] = j.primaryTriggerTpId;
  });

  // days on which a journey's last touchpoint falls — its panel ends there, so that
  // band needs an extra pad below the panel to keep it off the next day line
  const endsOnDay = new Set(model.journeys.map((j) => Math.max(...j.touchpoints.map((t) => t.day))));

  const byDay = {}; raw.forEach((r) => { (byDay[r.day] = byDay[r.day] || []).push(r); });
  const days = Object.keys(byDay).map(Number).sort((a, b) => a - b);

  const cards = [];
  const bands = [];
  let rowTop = o.topPad;
  days.forEach((day) => {
    const dcards = byDay[day];
    // base slot: stack same-column messages by ordinal
    const slot = {};
    const byCol = {}; dcards.forEach((r) => { (byCol[r.col] = byCol[r.col] || []).push(r); });
    Object.keys(byCol).forEach((c) => {
      byCol[c].sort((a, b) => a.tp.ordinal - b.tp.ordinal);
      byCol[c].forEach((r, k) => { slot[r.tp.touchpoint_id] = k; });
    });
    // push-down: an entry whose trigger is on this same day sits one slot below it
    // (dcards are in journey pre-order, so a trigger is always assigned before its child)
    dcards.forEach((r) => {
      const trig = entryTrigger[r.tp.touchpoint_id];
      if (trig != null && dayOfTp[trig] === day) {
        slot[r.tp.touchpoint_id] = Math.max(slot[r.tp.touchpoint_id], (slot[trig] ?? 0) + 1);
      }
    });
    // place each slot row. An entry card sits tight under its OWN header
    // (pad → header → pad), so its box is snug regardless of neighbours. A
    // continuation card sits at the row baseline — the tallest entry's card level —
    // so a same-day continuation stays parallel with the entry beside it. Entries
    // with shorter headers therefore sit a little higher (shorter box), with no slack.
    const bySlot = {}; dcards.forEach((r) => { const s = slot[r.tp.touchpoint_id]; (bySlot[s] = bySlot[s] || []).push(r); });
    const maxSlot = dcards.reduce((m, r) => Math.max(m, slot[r.tp.touchpoint_id]), 0);
    const cardTop = {};
    let y = rowTop;
    for (let s = 0; s <= maxSlot; s += 1) {
      const inSlot = bySlot[s] || [];
      const entries = inSlot.filter((r) => r.isEntry);
      const maxH = entries.length ? Math.max(...entries.map((r) => headerH(r.journeyId))) : 0;
      const baseline = entries.length ? o.pad + maxH + o.pad : (s === 0 ? o.pad : o.gap);
      inSlot.forEach((r) => {
        const off = r.isEntry ? o.pad + headerH(r.journeyId) + o.pad : baseline;
        cardTop[r.tp.touchpoint_id] = y + off;
      });
      const slotH = inSlot.length ? Math.max(...inSlot.map((r) => cardHeightOf(r.tp))) : o.cardH;
      y += baseline + slotH;
    }
    dcards.forEach((r) => {
      cards.push({
        touchpoint: r.tp, journeyId: r.journeyId, x: colX(r.journeyId),
        y: cardTop[r.tp.touchpoint_id],
        day, isEntry: r.isEntry, waitDays: isoToDays(r.tp.wait_time),
      });
    });
    // pad below the cards; an extra pad when a panel ends here so it clears the day line
    const rowHeight = (y + o.pad + (endsOnDay.has(day) ? o.pad : 0)) - rowTop;
    bands.push({ day, top: rowTop, height: rowHeight, count: dcards.length, label: `Day ${day + 1}` });
    rowTop += rowHeight;
  });

  return {
    cards, columns, bands,
    width: o.railLeft + 90 + laneCount * o.colW,
    height: rowTop + 80,
  };
}
